import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, Plus, Link2, Trash2, Loader2, AlertTriangle, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  useRisks, useAddRisk, useUpdateRisk, useLinkProject, useUnlinkProject,
  useAddControl, useUpdateControl, useDeleteControl,
  type DbRisk,
} from "@/hooks/useRiskManagement";

interface RiskProposal {
  action: "create" | "link";
  existing_risk_id: string | null;
  existing_risk_name?: string | null;
  name: string;
  description: string;
  category: string;
  inherent_probability: string;
  inherent_impact: string;
  ai_reasoning: string;
}

interface ControlProposal {
  name: string;
  description: string;
  probability_reduction: number;
  impact_reduction: number;
  responsible: string;
}


type RiskLevel = "muy_bajo" | "bajo" | "medio" | "alto" | "muy_alto";
type RiskCategory = "operacional" | "tecnologico" | "estrategico" | "cumplimiento" | "reputacional";
type ControlStatus = "pendiente" | "en_progreso" | "implementado";

const riskLevels: Record<RiskLevel, { label: string; value: number }> = {
  muy_bajo: { label: "Muy Bajo", value: 1 },
  bajo: { label: "Bajo", value: 2 },
  medio: { label: "Medio", value: 3 },
  alto: { label: "Alto", value: 4 },
  muy_alto: { label: "Muy Alto", value: 5 },
};

const categoryLabels: Record<RiskCategory, string> = {
  operacional: "Operacional",
  tecnologico: "Tecnológico",
  estrategico: "Estratégico",
  cumplimiento: "Cumplimiento",
  reputacional: "Reputacional",
};

const controlStatusLabels: Record<ControlStatus, { label: string; variant: "default" | "secondary" | "outline" }> = {
  pendiente: { label: "Pendiente", variant: "outline" },
  en_progreso: { label: "En Progreso", variant: "secondary" },
  implementado: { label: "Implementado", variant: "default" },
};

const calcScore = (p: RiskLevel, i: RiskLevel) => riskLevels[p].value * riskLevels[i].value;

const levelFromScore = (score: number) => {
  if (score <= 4) return { label: "Bajo", color: "text-emerald-500" };
  if (score <= 9) return { label: "Medio", color: "text-yellow-500" };
  if (score <= 16) return { label: "Alto", color: "text-orange-500" };
  return { label: "Crítico", color: "text-red-500" };
};

interface Props {
  projectId: string;
  projectName: string;
  organizationId: string | null;
  readOnly?: boolean;
}

export function ProjectRisksTab({ projectId, projectName, organizationId, readOnly = false }: Props) {
  const { data: risks = [], isLoading } = useRisks(organizationId || "all");
  const addRisk = useAddRisk();
  const updateRisk = useUpdateRisk();
  const linkProject = useLinkProject();
  const unlinkProject = useUnlinkProject();
  const addControl = useAddControl();
  const updateControl = useUpdateControl();
  const deleteControl = useDeleteControl();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [linkRiskId, setLinkRiskId] = useState<string>("");
  const [linkRelevance, setLinkRelevance] = useState("");
  const [controlRiskId, setControlRiskId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // AI: riesgos
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [riskProposals, setRiskProposals] = useState<RiskProposal[]>([]);
  const [riskSelected, setRiskSelected] = useState<Record<number, boolean>>({});

  // AI: controles
  const [ctrlAiRiskId, setCtrlAiRiskId] = useState<string | null>(null);
  const [ctrlAiLoading, setCtrlAiLoading] = useState(false);
  const [controlProposals, setControlProposals] = useState<ControlProposal[]>([]);
  const [ctrlSelected, setCtrlSelected] = useState<Record<number, boolean>>({});


  const [newRisk, setNewRisk] = useState({
    name: "", description: "", category: "operacional" as RiskCategory,
    inherent_probability: "medio" as RiskLevel, inherent_impact: "medio" as RiskLevel,
  });
  const [newControl, setNewControl] = useState({
    name: "", description: "", status: "pendiente" as ControlStatus,
    probability_reduction: 0, impact_reduction: 0, responsible: "", due_date: "",
  });

  const linkedRisks = (risks as DbRisk[]).filter(r =>
    (r.risk_project_links || []).some(l => l.project_id === projectId)
  );
  const linkableRisks = (risks as DbRisk[]).filter(r =>
    !(r.risk_project_links || []).some(l => l.project_id === projectId)
  );

  const handleCreateRisk = async () => {
    if (!newRisk.name.trim()) { toast.error("El nombre del riesgo es obligatorio"); return; }
    if (!organizationId) { toast.error("El proyecto no tiene organización asociada"); return; }
    setSaving(true);
    try {
      const score = calcScore(newRisk.inherent_probability, newRisk.inherent_impact);
      const created = await addRisk.mutateAsync({
        name: newRisk.name.trim(),
        description: newRisk.description,
        category: newRisk.category,
        organization_id: organizationId,
        inherent_probability: newRisk.inherent_probability,
        inherent_impact: newRisk.inherent_impact,
        inherent_score: score,
        residual_probability: newRisk.inherent_probability,
        residual_impact: newRisk.inherent_impact,
        residual_score: score,
        source: "manual",
      });
      await linkProject.mutateAsync({ risk_id: created.id, project_id: projectId, relevance: "alta" });
      toast.success("Riesgo creado y vinculado al proyecto");
      setIsCreateOpen(false);
      setNewRisk({ name: "", description: "", category: "operacional", inherent_probability: "medio", inherent_impact: "medio" });
    } catch (err: any) {
      toast.error(err?.message || "No se pudo crear el riesgo");
    } finally {
      setSaving(false);
    }
  };

  const handleLink = async () => {
    if (!linkRiskId) { toast.error("Selecciona un riesgo"); return; }
    setSaving(true);
    try {
      await linkProject.mutateAsync({ risk_id: linkRiskId, project_id: projectId, relevance: linkRelevance || "media" });
      toast.success("Riesgo vinculado");
      setIsLinkOpen(false);
      setLinkRiskId("");
      setLinkRelevance("");
    } catch (err: any) {
      toast.error(err?.message || "No se pudo vincular el riesgo");
    } finally {
      setSaving(false);
    }
  };

  const handleAddControl = async () => {
    if (!controlRiskId) return;
    if (!newControl.name.trim()) { toast.error("El nombre del control es obligatorio"); return; }
    setSaving(true);
    try {
      await addControl.mutateAsync({
        risk_id: controlRiskId,
        name: newControl.name.trim(),
        description: newControl.description,
        status: newControl.status,
        probability_reduction: Number(newControl.probability_reduction) || 0,
        impact_reduction: Number(newControl.impact_reduction) || 0,
        responsible: newControl.responsible,
        due_date: newControl.due_date || undefined,
        source: "manual",
      });
      toast.success("Control agregado");
      setControlRiskId(null);
      setNewControl({ name: "", description: "", status: "pendiente", probability_reduction: 0, impact_reduction: 0, responsible: "", due_date: "" });
    } catch (err: any) {
      toast.error(err?.message || "No se pudo agregar el control");
    } finally {
      setSaving(false);
    }
  };

  const invokeFn = async (name: string, body: any) => {
    const { data, error } = await supabase.functions.invoke(name, { body });
    if (error) {
      let details = error.message;
      try { details = (await (error as any).context?.text?.()) || details; } catch { /* noop */ }
      try {
        const parsed = JSON.parse(details);
        details = parsed.error || details;
      } catch { /* noop */ }
      throw new Error(details);
    }
    if ((data as any)?.error) throw new Error((data as any).error);
    return data as any;
  };

  const handleIdentifyRisks = async () => {
    setAiOpen(true);
    setAiLoading(true);
    setRiskProposals([]);
    setRiskSelected({});
    try {
      const data = await invokeFn("identify-project-risks", { project_id: projectId });
      const proposals: RiskProposal[] = data?.proposals || [];
      setRiskProposals(proposals);
      setRiskSelected(Object.fromEntries(proposals.map((_, i) => [i, true])));
      if (proposals.length === 0) toast.info("La IA no propuso riesgos para este proyecto");
    } catch (err: any) {
      toast.error(err?.message || "No se pudieron identificar riesgos");
      setAiOpen(false);
    } finally {
      setAiLoading(false);
    }
  };

  const handleApplyRiskProposals = async () => {
    if (!organizationId) { toast.error("El proyecto no tiene organización asociada"); return; }
    const chosen = riskProposals.filter((_, i) => riskSelected[i]);
    if (chosen.length === 0) { toast.error("Selecciona al menos un riesgo"); return; }
    setSaving(true);
    let created = 0, linked = 0;
    try {
      for (const p of chosen) {
        const prob = (p.inherent_probability as RiskLevel) || "medio";
        const imp = (p.inherent_impact as RiskLevel) || "medio";
        const score = calcScore(prob, imp);
        if (p.action === "link" && p.existing_risk_id) {
          // Reutilizar riesgo existente (ajustando su redacción a una versión más genérica)
          await updateRisk.mutateAsync({ id: p.existing_risk_id, name: p.name, description: p.description });
          const already = (risks as DbRisk[])
            .find(r => r.id === p.existing_risk_id)?.risk_project_links
            ?.some(l => l.project_id === projectId);
          if (!already) {
            await linkProject.mutateAsync({ risk_id: p.existing_risk_id, project_id: projectId, relevance: "alta" });
          }
          linked++;
        } else {
          const risk = await addRisk.mutateAsync({
            name: p.name,
            description: p.description,
            category: (p.category as RiskCategory) || "operacional",
            organization_id: organizationId,
            inherent_probability: prob,
            inherent_impact: imp,
            inherent_score: score,
            residual_probability: prob,
            residual_impact: imp,
            residual_score: score,
            source: "ai",
            ai_reasoning: p.ai_reasoning,
          });
          await linkProject.mutateAsync({ risk_id: risk.id, project_id: projectId, relevance: "alta" });
          created++;
        }
      }
      toast.success(`${created} riesgo(s) creado(s) y ${linked} vinculado(s)`);
      setAiOpen(false);
      setRiskProposals([]);
    } catch (err: any) {
      toast.error(err?.message || "No se pudieron aplicar los riesgos");
    } finally {
      setSaving(false);
    }
  };

  const handleIdentifyControls = async (riskId: string) => {
    setCtrlAiRiskId(riskId);
    setCtrlAiLoading(true);
    setControlProposals([]);
    setCtrlSelected({});
    try {
      const data = await invokeFn("identify-risk-controls", { risk_id: riskId, project_id: projectId });
      const proposals: ControlProposal[] = data?.controls || [];
      setControlProposals(proposals);
      setCtrlSelected(Object.fromEntries(proposals.map((_, i) => [i, true])));
      if (proposals.length === 0) toast.info("La IA no propuso controles");
    } catch (err: any) {
      toast.error(err?.message || "No se pudieron identificar controles");
      setCtrlAiRiskId(null);
    } finally {
      setCtrlAiLoading(false);
    }
  };

  const handleApplyControlProposals = async () => {
    if (!ctrlAiRiskId) return;
    const chosen = controlProposals.filter((_, i) => ctrlSelected[i]);
    if (chosen.length === 0) { toast.error("Selecciona al menos un control"); return; }
    setSaving(true);
    try {
      for (const c of chosen) {
        await addControl.mutateAsync({
          risk_id: ctrlAiRiskId,
          name: c.name,
          description: c.description,
          status: "pendiente",
          probability_reduction: c.probability_reduction,
          impact_reduction: c.impact_reduction,
          responsible: c.responsible,
          source: "ai",
        });
      }
      toast.success(`${chosen.length} control(es) agregado(s)`);
      setCtrlAiRiskId(null);
      setControlProposals([]);
    } catch (err: any) {
      toast.error(err?.message || "No se pudieron agregar los controles");
    } finally {
      setSaving(false);
    }
  };


  if (isLoading) {
    return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" /> Riesgos del Proyecto
            </CardTitle>
            <CardDescription>Riesgos y controles asociados a {projectName}</CardDescription>
          </div>
          {!readOnly && (
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={handleIdentifyRisks} disabled={aiLoading}>
              {aiLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              Identificar riesgos con IA
            </Button>
            <Button variant="outline" size="sm" onClick={() => setIsLinkOpen(true)}>
              <Link2 className="w-4 h-4 mr-1.5" /> Vincular
            </Button>

            <Button size="sm" className="gradient-primary" onClick={() => setIsCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-1.5" /> Nuevo riesgo
            </Button>
          </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {linkedRisks.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              Este proyecto aún no tiene riesgos identificados.
            </p>
          )}

          {linkedRisks.map((risk) => {
            const inherent = risk.inherent_score || 0;
            const residual = risk.residual_score || 0;
            const link = (risk.risk_project_links || []).find(l => l.project_id === projectId);
            return (
              <div key={risk.id} className="rounded-xl border border-border p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center flex-wrap gap-2">
                      <h4 className="font-semibold text-foreground">{risk.name}</h4>
                      <Badge variant="outline">{categoryLabels[(risk.category as RiskCategory) || "operacional"]}</Badge>
                      <Badge variant="secondary" className={levelFromScore(residual || inherent).color}>
                        {levelFromScore(residual || inherent).label}
                      </Badge>
                    </div>
                    {risk.description && <p className="text-sm text-muted-foreground mt-1">{risk.description}</p>}
                    <p className="text-xs text-muted-foreground mt-1">
                      Inherente: {inherent} · Residual: {residual}
                      {link?.relevance ? ` · Relevancia: ${link.relevance}` : ""}
                    </p>
                  </div>
                  {!readOnly && (
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleIdentifyControls(risk.id)}
                      disabled={ctrlAiLoading && ctrlAiRiskId === risk.id}
                    >
                      {ctrlAiLoading && ctrlAiRiskId === risk.id
                        ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                      Controles IA
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setControlRiskId(risk.id)}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Control
                    </Button>

                    {link && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            await unlinkProject.mutateAsync(link.id);
                            toast.success("Riesgo desvinculado");
                          } catch (err: any) {
                            toast.error(err?.message || "No se pudo desvincular");
                          }
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                  )}
                </div>

                <div className="space-y-2">
                  {(risk.risk_controls || []).length === 0 && (
                    <p className="text-xs text-muted-foreground">Sin controles definidos.</p>
                  )}
                  {(risk.risk_controls || []).map((ctrl) => {
                    const st = (ctrl.status as ControlStatus) || "pendiente";
                    return (
                      <div key={ctrl.id} className="flex items-center gap-3 bg-muted/40 rounded-lg p-2.5">
                        {st === "implementado"
                          ? <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                          : <AlertTriangle className="w-4 h-4 text-warning shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{ctrl.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {ctrl.responsible || "Sin responsable"}
                            {ctrl.due_date ? ` · ${ctrl.due_date}` : ""}
                            {` · -${ctrl.probability_reduction || 0}P / -${ctrl.impact_reduction || 0}I`}
                          </p>
                        </div>
                        <Select
                          value={st}
                          disabled={readOnly}
                          onValueChange={async (v) => {
                            try {
                              await updateControl.mutateAsync({ id: ctrl.id, status: v as ControlStatus });
                            } catch (err: any) {
                              toast.error(err?.message || "No se pudo actualizar el control");
                            }
                          }}
                        >
                          <SelectTrigger className="w-[150px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(controlStatusLabels).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!readOnly && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={async () => {
                            try {
                              await deleteControl.mutateAsync(ctrl.id);
                              toast.success("Control eliminado");
                            } catch (err: any) {
                              toast.error(err?.message || "No se pudo eliminar");
                            }
                          }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Crear riesgo */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo riesgo</DialogTitle>
            <DialogDescription>Se vinculará automáticamente a este proyecto</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={newRisk.name} onChange={(e) => setNewRisk({ ...newRisk, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Descripción</Label>
              <Textarea rows={3} value={newRisk.description} onChange={(e) => setNewRisk({ ...newRisk, description: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Categoría</Label>
              <Select value={newRisk.category} onValueChange={(v) => setNewRisk({ ...newRisk, category: v as RiskCategory })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(categoryLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Probabilidad inherente</Label>
                <Select value={newRisk.inherent_probability} onValueChange={(v) => setNewRisk({ ...newRisk, inherent_probability: v as RiskLevel })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(riskLevels).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Impacto inherente</Label>
                <Select value={newRisk.inherent_impact} onValueChange={(v) => setNewRisk({ ...newRisk, inherent_impact: v as RiskLevel })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(riskLevels).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleCreateRisk} disabled={saving} className="gradient-primary">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vincular riesgo */}
      <Dialog open={isLinkOpen} onOpenChange={setIsLinkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vincular riesgo existente</DialogTitle>
            <DialogDescription>Asocia un riesgo ya identificado a este proyecto</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Riesgo</Label>
              <Select value={linkRiskId} onValueChange={setLinkRiskId}>
                <SelectTrigger><SelectValue placeholder="Seleccionar riesgo" /></SelectTrigger>
                <SelectContent>
                  {linkableRisks.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Relevancia</Label>
              <Select value={linkRelevance} onValueChange={setLinkRelevance}>
                <SelectTrigger><SelectValue placeholder="Media" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="media">Media</SelectItem>
                  <SelectItem value="baja">Baja</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLinkOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleLink} disabled={saving} className="gradient-primary">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Vincular
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Agregar control */}
      <Dialog open={!!controlRiskId} onOpenChange={(o) => !o && setControlRiskId(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo control</DialogTitle>
            <DialogDescription>Define una medida de mitigación para el riesgo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={newControl.name} onChange={(e) => setNewControl({ ...newControl, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Descripción</Label>
              <Textarea rows={2} value={newControl.description} onChange={(e) => setNewControl({ ...newControl, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Estado</Label>
                <Select value={newControl.status} onValueChange={(v) => setNewControl({ ...newControl, status: v as ControlStatus })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(controlStatusLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Responsable</Label>
                <Input value={newControl.responsible} onChange={(e) => setNewControl({ ...newControl, responsible: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Reducción probabilidad</Label>
                <Input type="number" min={0} max={4} value={newControl.probability_reduction}
                  onChange={(e) => setNewControl({ ...newControl, probability_reduction: Number(e.target.value) })} />
              </div>
              <div className="space-y-1.5">
                <Label>Reducción impacto</Label>
                <Input type="number" min={0} max={4} value={newControl.impact_reduction}
                  onChange={(e) => setNewControl({ ...newControl, impact_reduction: Number(e.target.value) })} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Fecha límite</Label>
                <Input type="date" value={newControl.due_date} onChange={(e) => setNewControl({ ...newControl, due_date: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setControlRiskId(null)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleAddControl} disabled={saving} className="gradient-primary">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Riesgos propuestos por IA */}
      <Dialog open={aiOpen} onOpenChange={(o) => { if (!saving) setAiOpen(o); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" /> Riesgos identificados con IA
            </DialogTitle>
            <DialogDescription>
              Revisa las propuestas. Los riesgos que ya existen se vinculan (y se ajusta su redacción) en vez de duplicarse.
            </DialogDescription>
          </DialogHeader>

          {aiLoading ? (
            <div className="flex flex-col items-center gap-3 py-10 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <p className="text-sm">Analizando el proyecto...</p>
            </div>
          ) : (
            <div className="space-y-3">
              {riskProposals.map((p, i) => (
                <div key={i} className="rounded-xl border border-border p-3 flex gap-3">
                  <Checkbox
                    checked={!!riskSelected[i]}
                    onCheckedChange={(v) => setRiskSelected((s) => ({ ...s, [i]: !!v }))}
                    className="mt-1"
                  />
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-foreground">{p.name}</p>
                      <Badge variant={p.action === "link" ? "secondary" : "default"}>
                        {p.action === "link" ? "Vincular existente" : "Nuevo"}
                      </Badge>
                      <Badge variant="outline">{categoryLabels[(p.category as RiskCategory) || "operacional"]}</Badge>
                    </div>
                    {p.action === "link" && p.existing_risk_name && (
                      <p className="text-xs text-muted-foreground">Riesgo existente: {p.existing_risk_name}</p>
                    )}
                    <p className="text-sm text-muted-foreground">{p.description}</p>
                    <p className="text-xs text-muted-foreground">
                      Probabilidad: {riskLevels[(p.inherent_probability as RiskLevel) || "medio"].label} · Impacto: {riskLevels[(p.inherent_impact as RiskLevel) || "medio"].label}
                    </p>
                    {p.ai_reasoning && <p className="text-xs text-muted-foreground italic">{p.ai_reasoning}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAiOpen(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleApplyRiskProposals} disabled={saving || aiLoading || riskProposals.length === 0} className="gradient-primary">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Aplicar seleccionados
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Controles propuestos por IA */}
      <Dialog
        open={!!ctrlAiRiskId && (ctrlAiLoading || controlProposals.length > 0)}
        onOpenChange={(o) => { if (!o && !saving) { setCtrlAiRiskId(null); setControlProposals([]); } }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" /> Controles sugeridos por IA
            </DialogTitle>
            <DialogDescription>Selecciona los controles a agregar a este riesgo</DialogDescription>
          </DialogHeader>

          {ctrlAiLoading ? (
            <div className="flex flex-col items-center gap-3 py-10 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <p className="text-sm">Analizando el riesgo...</p>
            </div>
          ) : (
            <div className="space-y-3">
              {controlProposals.map((c, i) => (
                <div key={i} className="rounded-xl border border-border p-3 flex gap-3">
                  <Checkbox
                    checked={!!ctrlSelected[i]}
                    onCheckedChange={(v) => setCtrlSelected((s) => ({ ...s, [i]: !!v }))}
                    className="mt-1"
                  />
                  <div className="min-w-0 space-y-1">
                    <p className="font-medium text-foreground">{c.name}</p>
                    <p className="text-sm text-muted-foreground">{c.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {c.responsible || "Sin responsable"} · -{c.probability_reduction}% probabilidad / -{c.impact_reduction}% impacto
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCtrlAiRiskId(null); setControlProposals([]); }} disabled={saving}>Cancelar</Button>
            <Button onClick={handleApplyControlProposals} disabled={saving || ctrlAiLoading || controlProposals.length === 0} className="gradient-primary">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Agregar seleccionados
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

